import { MembershipSettingSection } from '../../S/MembershipSettingForm/MembershipSettingSection';
import { MembershipFilter } from '../../S/MembershipFilter/MembershipFilter';
import { updateUrlParams } from '../../../../Util/S/Function/util';
import { hydrateRuneAndGetView } from '../../../../../services/MarppleShop/shared/client/utils';
import { MaxPerUserInputField } from '../../../../../services/MarppleShop/marppleShopStudioApp/components/cells/ProductEditorField/views/MaxPerUserInputField';
import {
  QuantityLimitInputField,
  QuantityLimitInputResult,
} from '../../../../../services/MarppleShop/marppleShopStudioApp/components/cells/ProductEditorField/views/QuantityLimitInputField';
import { SaleEndDateInputField } from '../../../../../services/MarppleShop/marppleShopStudioApp/components/cells/ProductEditorField/views/SaleEndDateInputField';
import { SaleStartDateInputField } from '../../../../../services/MarppleShop/marppleShopStudioApp/components/cells/ProductEditorField/views/SaleStartDateInputField';
import { compact, each, flatMap, pipe, toArray, uniqBy } from '@fxts/core';
import { quantityMaxPerUser, validateSaleDateRange } from '../events';
import { ProductEditorError } from '../../../../../services/MarppleShop/marppleShopStudioApp/components/cells/ProductEditorField/types/type';
import { Toggle } from '@shared/rune-component/Toggle/Toggle.ts';

export const initMembershipHelper = (): MembershipSettingSection | undefined => {
  return hydrateRuneAndGetView(MembershipSettingSection);
};

export const initMembershipProductFilterHelper = (): {
  view: MembershipFilter;
  getUpdatedUrl: (url: string) => string;
} | null => {
  const view = hydrateRuneAndGetView(MembershipFilter);
  if (view) {
    const getUpdatedUrl = (url: string) => {
      return updateUrlParams(url, { only_membership: view.isChecked() || undefined });
    };
    return { view, getUpdatedUrl };
  } else {
    return null;
  }
};

export const initProductEditorInputHelper = () => {
  const max_per_user_view = hydrateRuneAndGetView(MaxPerUserInputField);
  const quantity_limit_view = hydrateRuneAndGetView(QuantityLimitInputField);
  const sale_start_view = hydrateRuneAndGetView(SaleStartDateInputField);
  const sale_end_view = hydrateRuneAndGetView(SaleEndDateInputField);

  const views = pipe(
    [max_per_user_view, quantity_limit_view, sale_start_view, sale_end_view],
    compact,
    toArray,
  );

  return {
    views,
    view: {
      한정판매수량: quantity_limit_view,
      인당구매수량제한: max_per_user_view,
      판매시작일: sale_start_view,
      판매종료일: sale_end_view,
    },
    validate: (): ProductEditorError[] => {
      const combined_errors = [
        ...validateSaleDateRange(sale_start_view, sale_end_view),
        ...quantityMaxPerUser(max_per_user_view, quantity_limit_view),
      ];
      const single_errors = pipe(
        views,
        flatMap((view) => view.validate()),
        toArray,
      );
      const errors = [...combined_errors, ...single_errors];

      pipe(
        errors,
        uniqBy((error) => error.target.toString()),
        each((error) => error.target.setErrorMessage(error.message)),
      );

      return errors;
    },
    getValue: (): {
      sell_start_at?: Date;
      sell_end_at?: Date;
      quantity_limit?: QuantityLimitInputResult;
      max_per_user?: number;
    } => {
      return {
        sell_start_at: sale_start_view?.getValue(),
        sell_end_at: sale_end_view?.getValue(),
        quantity_limit: quantity_limit_view?.getValue(),
        max_per_user: max_per_user_view?.getValue(),
      };
    },
  };
};

export const initProductEditorToggle = (): Toggle | null => {
  const view = hydrateRuneAndGetView(Toggle);
  if (view) {
    return view;
  } else {
    return null;
  }
};

export type ProductEditorInputHelper = ReturnType<typeof initProductEditorInputHelper>;
